import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AcademicCapIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  CogIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  GlassCard,
  PremiumCTA,
  filterPillClass,
  btnPrimaryClass,
} from '../premium/PremiumPageUI';

const trainingCourses = [
  { id: 1, title: 'Web Development Masterclass', category: 'development', duration: '8 weeks', level: 'Intermediate', description: 'React, Node.js, REST APIs, and cloud deployment.', topics: ['HTML5 & CSS3', 'JavaScript ES6+', 'React', 'Node.js', 'RESTful APIs', 'Deployment'] },
  { id: 2, title: 'Cybersecurity Fundamentals', category: 'security', duration: '6 weeks', level: 'Beginner', description: 'Threat detection, network security, and incident response.', topics: ['Security Fundamentals', 'Threat Detection', 'Network Security', 'Encryption', 'Audits', 'Incident Response'] },
  { id: 3, title: 'Data Science & Analytics', category: 'data', duration: '10 weeks', level: 'Advanced', description: 'ML, statistical analysis, and predictive modeling.', topics: ['Python', 'Statistics', 'Machine Learning', 'Visualization', 'Big Data', 'Modeling'] },
  { id: 4, title: 'Cloud Architecture', category: 'cloud', duration: '8 weeks', level: 'Intermediate', description: 'AWS, Azure, Kubernetes, and DevOps integration.', topics: ['AWS', 'Azure', 'GCP', 'Security', 'Serverless', 'DevOps'] },
];

const consultingServices = [
  { id: 1, title: 'Digital Transformation', description: 'Navigate the digital landscape with technologies that drive growth and efficiency.', icon: ChartBarIcon },
  { id: 2, title: 'IT Strategy & Planning', description: 'Comprehensive IT strategies aligned with business goals and innovation.', icon: LightBulbIcon },
  { id: 3, title: 'Security Assessment', description: 'Evaluate security posture with actionable recommendations against cyber threats.', icon: ShieldCheckIcon },
  { id: 4, title: 'Process Optimization', description: 'Streamline operations with technology solutions that reduce cost and friction.', icon: CogIcon },
];

const experts = [
  { name: 'Dr. Sarah Johnson', role: 'Chief Technology Officer', expertise: ['Cloud Architecture', 'Digital Transformation'] },
  { name: 'Michael Chen', role: 'Lead Security Consultant', expertise: ['Cybersecurity', 'Penetration Testing'] },
  { name: 'Priya Patel', role: 'Senior Data Scientist', expertise: ['Machine Learning', 'AI Implementation'] },
];

const faqs = [
  { question: 'How are training programs structured?', answer: 'Live instructor-led sessions, hands-on projects, self-paced materials, weekly assignments, and a capstone project.' },
  { question: 'Can programs be customized for our organization?', answer: 'Yes—we develop tailored curricula aligned with your skill gaps and business objectives.' },
  { question: 'What is the consulting process like?', answer: 'Assessment, strategic recommendations, implementation planning, and ongoing collaborative support.' },
  { question: 'Do you offer ongoing support?', answer: 'Post-training packages and follow-up consulting ensure long-term success and adoption.' },
];

const levelClass = (level) =>
  level === 'Beginner'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : level === 'Intermediate'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';

export default function TrainingConsulting() {
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeConsulting, setActiveConsulting] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = ['all', 'development', 'security', 'data', 'cloud'];
  const filtered = trainingCourses.filter((c) => filterCategory === 'all' || c.category === filterCategory);

  return (
    <PageShell>
      <PremiumHero
        badge="Training & Consulting"
        title="Expert"
        highlight="Growth Programs"
        subtitle="Elevate your team's capabilities and transform your business with industry-leading training and strategic consulting—from assessment to scale."
        variant="education"
        stats={[
          { value: '20+', label: 'Courses' },
          { value: '50+', label: 'Consultants' },
          { value: '98%', label: 'Satisfaction' },
          { value: 'Hybrid', label: 'Delivery' },
        ]}
        actions={[
          { label: 'View Programs', primary: true, onClick: () => document.getElementById('programs')?.scrollIntoView({ behavior: 'smooth' }) },
          { label: 'Book Consultation', href: '/contact' },
        ]}
      />

      <PremiumSection id="programs">
        <SectionHeader badge="Training" title="Professional" highlight="Programs" />
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button key={cat} type="button" onClick={() => setFilterCategory(cat)} className={filterPillClass(filterCategory === cat)}>
              {cat === 'all' ? 'All Programs' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filtered.map((course, i) => (
            <motion.div key={course.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} whileHover={{ y: -6 }}>
              <GlassCard className="p-6 h-full flex flex-col">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-bold text-slate-900 dark:text-white text-lg leading-tight">{course.title}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${levelClass(course.level)}`}>{course.level}</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-gray-300 flex-grow mb-4">{course.description}</p>
                <div className="flex items-center gap-1 text-xs text-slate-500 mb-4">
                  <ClockIcon className="w-4 h-4" /> {course.duration}
                </div>
                <button type="button" onClick={() => setSelectedCourse(course)} className="w-full py-2.5 text-sm font-semibold text-[#2563EB] border border-[#2563EB]/30 rounded-xl hover:bg-[#2563EB] hover:text-white transition-all">
                  View Details
                </button>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </PremiumSection>

      <AnimatePresence>
        {selectedCourse && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedCourse(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }} onClick={(e) => e.stopPropagation()} className="hero-glass-card rounded-3xl max-w-lg w-full p-8 relative premium-border-glow">
              <button type="button" onClick={() => setSelectedCourse(null)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-gray-700">
                <XMarkIcon className="w-5 h-5" />
              </button>
              <h2 className="text-2xl font-bold mb-2">{selectedCourse.title}</h2>
              <p className="text-sm text-slate-600 dark:text-gray-300 mb-6">{selectedCourse.description}</p>
              <ul className="space-y-2 mb-6">
                {selectedCourse.topics.map((t) => (
                  <li key={t} className="flex gap-2 text-sm"><AcademicCapIcon className="w-4 h-4 text-[#2563EB]" />{t}</li>
                ))}
              </ul>
              <Link to="/contact" className={btnPrimaryClass}>Enroll Now</Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PremiumSection alt>
        <SectionHeader badge="Consulting" title="Strategic" highlight="Services" />
        <GlassCard className="overflow-hidden" hover={false}>
          <div className="flex overflow-x-auto border-b border-slate-200/80 dark:border-gray-700">
            {consultingServices.map((s, i) => (
              <button key={s.id} type="button" onClick={() => setActiveConsulting(i)} className={`px-6 py-4 text-sm font-semibold whitespace-nowrap ${activeConsulting === i ? 'text-[#2563EB] border-b-2 border-[#2563EB] bg-[#2563EB]/5' : 'text-slate-500'}`}>
                {s.title}
              </button>
            ))}
          </div>
          <div className="p-8 grid md:grid-cols-2 gap-8">
            <div>
              {(() => {
                const s = consultingServices[activeConsulting];
                const Icon = s.icon;
                return (
                  <>
                    <Icon className="w-12 h-12 text-[#2563EB] mb-4" />
                    <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                    <p className="text-slate-600 dark:text-gray-300 text-sm leading-relaxed">{s.description}</p>
                  </>
                );
              })()}
            </div>
            <div className="space-y-4">
              {['Assessment', 'Strategy', 'Implementation', 'Optimization'].map((step, i) => (
                <div key={step} className="flex gap-4">
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2563EB] to-cyan-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <div>
                    <h4 className="font-semibold text-sm">{step}</h4>
                    <p className="text-xs text-slate-500 dark:text-gray-400">Expert-led phase with measurable outcomes.</p>
                  </div>
                </div>
              ))}
              <Link to="/contact" className={`${btnPrimaryClass} mt-4 inline-flex`}>Schedule Consultation</Link>
            </div>
          </div>
        </GlassCard>
      </PremiumSection>

      <PremiumSection>
        <SectionHeader badge="Team" title="Expert" highlight="Instructors" />
        <div className="grid md:grid-cols-3 gap-6">
          {experts.map((ex, i) => (
            <GlassCard key={ex.name} className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#2563EB]/20 to-cyan-500/10 flex items-center justify-center text-2xl font-bold text-[#2563EB]">
                {ex.name.split(' ').map((n) => n[0]).join('')}
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white">{ex.name}</h3>
              <p className="text-sm text-[#2563EB] mb-3">{ex.role}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {ex.expertise.map((sk) => (
                  <span key={sk} className="text-xs px-2.5 py-1 rounded-full hero-glass-tag">{sk}</span>
                ))}
              </div>
            </GlassCard>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection alt>
        <SectionHeader badge="FAQ" title="Frequently Asked" highlight="Questions" />
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <GlassCard key={faq.question} className="overflow-hidden" hover={false}>
              <button type="button" onClick={() => setExpandedFaq(expandedFaq === index ? null : index)} className="w-full flex justify-between items-center p-5 text-left font-semibold">
                {faq.question}
                <span className={`text-[#2563EB] transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`}>▼</span>
              </button>
              <AnimatePresence>
                {expandedFaq === index && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <p className="px-5 pb-5 text-sm text-slate-600 dark:text-gray-300">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          ))}
        </div>
      </PremiumSection>

      <PremiumCTA
        title="Ready to Transform Your Business?"
        subtitle="Schedule a consultation or explore our academy programs to upskill your team."
        primary={{ label: 'Schedule Consultation', href: '/contact' }}
        secondary={{ label: 'Nisunajni Academy', href: '/academy' }}
      />
    </PageShell>
  );
}
